import { useCallback } from "react";
import { calculatePremium } from "../../../premium/domain/calculatePremium";
import { applyPremiumRecalcToPayload } from "../../shared/bondWizardStepHelpers";
import { getBondFormKindConfig } from "../../shared/bondFormConfig";
import useBondFormBase, { useStandardBondKindLogic } from "../../shared/hooks/useBondFormBase";
import {
  createProbateBondWithAutoApprove,
  submitSingleProbateBond,
  submitSplitBondsByRepresentative,
} from "../probateBondFormHelpers";

const config = getBondFormKindConfig("probate");

function useProbateKindLogic(ctx) {
  const { globalState, setIsLoading } = ctx;
  const { validateStepData, generatePayload } = useStandardBondKindLogic(config, ctx);

  const createBond = useCallback(
    async (payload, options = {}) => {
      await createProbateBondWithAutoApprove(config, ctx, payload, options);
    },
    [ctx]
  );

  const handleFinish = useCallback(async () => {
    setIsLoading(true);
    const payload = generatePayload(globalState);
    applyPremiumRecalcToPayload(
      payload,
      calculatePremium,
      config.getPremiumRecalcInputs(globalState)
    );

    const isSplitByPR =
      globalState.basicInformation?.is_split_by_personal_representatives === "Yes";
    const principalCount = Number.parseInt(
      globalState.basicInformation?.PrincipalNumber || 1,
      10
    );

    if (isSplitByPR && principalCount > 1) {
      await submitSplitBondsByRepresentative(
        config,
        ctx,
        payload,
        principalCount,
        createBond
      );
      return;
    }

    await submitSingleProbateBond(ctx, payload, createBond);
  }, [ctx, globalState, generatePayload, createBond, setIsLoading]);

  return { validateStepData, handleFinish, createBond };
}

export default function useProbateBondForm() {
  return useBondFormBase({
    config,
    logContext: "useProbateBondForm",
    useBondKindLogic: useProbateKindLogic,
  });
}
